import { z } from "zod";
import type { Profile, UpdateProfile } from "../types/profile";
import { workClient } from "./workClient";

const profileSchema = z.object({
  country: z.string().optional().default(""),
  language: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phone: z.string(),
  phoneCountryCode: z.string(),
  timezone: z.string(),
});

export function parseProfileResponse(response: unknown): Profile {
  return profileSchema.parse(response);
}

export class ProfileRequestError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function assertResponse(response: Response) {
  if (response.ok) return;
  const body: unknown = await response.json().catch(() => null);
  const message =
    typeof body === "object" &&
    body !== null &&
    "error" in body &&
    typeof body.error === "string"
      ? body.error
      : "Unable to save your profile. Try again.";
  throw new ProfileRequestError(response.status, message);
}

export async function fetchProfile(): Promise<Profile> {
  const response = await workClient.v1["current-user"].profile.$get();
  await assertResponse(response);
  return parseProfileResponse(await response.json());
}

export async function updateProfile(input: UpdateProfile): Promise<Profile> {
  const response = await workClient.v1["current-user"].profile.$patch({
    json: input,
  });
  await assertResponse(response);
  return parseProfileResponse(await response.json());
}
